import { useEffect, useMemo, useState } from 'react';
import * as R from 'remeda';
import { useIntl } from 'react-intl';
import { Queue } from '@rossum/api-client/queues';
import {
  ChoiceDialog,
  SelectDialogProps,
} from '@rossum/components/choice-dialog/ChoiceDialog';
import { useWorkspacesWithQueues } from '../hooks/useWorkspacesWithQueues';
import { QueueSelectList } from './QueueSelectList';

type QueueDialogProps = Omit<
  SelectDialogProps,
  'onConfirm' | 'submitText' | 'cancelText' | 'children'
> & {
  preSelectedQueueIds?: number[];
  onConfirm: (queues: Queue[]) => void;
  submitText?: SelectDialogProps['submitText'];
  cancelText?: SelectDialogProps['cancelText'];
};

export const MultipleQueueDialog = ({
  onConfirm,
  submitText,
  cancelText,
  isDisabled,
  preSelectedQueueIds,
  ...props
}: QueueDialogProps) => {
  const intl = useIntl();
  const [selectedQueueIds, setSelectedQueueIds] = useState<
    number[] | undefined
  >(preSelectedQueueIds);

  const { workspacesWithQueues: queuesByWorkspace, isLoading } =
    useWorkspacesWithQueues({
      enableQueries: true,
    });

  const selectedQueuesMap = useMemo(
    () =>
      R.pipe(
        queuesByWorkspace ?? [],
        R.flatMap(workspace => workspace.queues),
        R.mapToObj(queue => [queue.id, queue])
      ),
    [queuesByWorkspace]
  );

  const selectedQueues = useMemo(
    () =>
      R.pipe(
        selectedQueueIds ?? [],
        R.map(id => selectedQueuesMap[id]),
        R.filter(R.isNonNullish)
      ),
    [selectedQueueIds, selectedQueuesMap]
  );

  useEffect(() => {
    // when dialog is closed ensure preSelectedIds are in tact
    if (!props.open && !R.isDeepEqual(preSelectedQueueIds, selectedQueueIds))
      setSelectedQueueIds(preSelectedQueueIds);
  }, [props.open, preSelectedQueueIds, selectedQueueIds]);

  return (
    <ChoiceDialog
      submitText={
        submitText ??
        intl.formatMessage({
          id: 'components.selectQueueDialog.submit',
        })
      }
      cancelText={
        cancelText ??
        intl.formatMessage({
          id: 'components.selectQueueModal.cancel',
        })
      }
      isDisabled={isDisabled || selectedQueueIds === undefined}
      {...props}
      onConfirm={() => {
        if (selectedQueues) {
          onConfirm(selectedQueues);
        }
      }}
    >
      {props.open && (
        <QueueSelectList
          selectedQueues={selectedQueueIds ?? []}
          onSelect={queues => setSelectedQueueIds(queues)}
          queuesByWorkspace={queuesByWorkspace ?? []}
          isLoading={isLoading}
        />
      )}
    </ChoiceDialog>
  );
};
